import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import CompareChartBase from './compare_chart_base';
import { t } from '../javascript/lib/i18n';

// Sleep palette shared with the per-org Sleep summary chart
// (`summary_charts/sleep_chart_controller.js`) so the bedtimes / waketimes /
// out-of-bed series read as the same data across the two views.
const AGGREGATE_COLORS = {
  bedtimes: '#0767f7',
  waketimes: '#aacafa',
  out_of_bed: '#643bba',
};

const TINT_OPACITY = {
  bedtimes: '88',
  waketimes: '55',
};

export default class extends CompareChartBase {
  connect() {
    this.visibleKinds = new Set(['bedtimes', 'waketimes', 'out_of_bed']);
    super.connect();
  }

  get compareIds() {
    return new Set(
      this.chartDataValue.datasets.map((ds) => ds.compareId).filter(Boolean)
    );
  }

  initChart() {
    this.chart?.destroy();

    const ctx = this.canvasTarget.getContext('2d');
    const theme = this.chartTheme();

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.chartDataValue.labels,
        datasets: this.chartDataValue.datasets.map((dataset) =>
          this.buildDataset(dataset)
        ),
      },
      plugins: [ChartDataLabels],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { top: 8 } },
        scales: {
          x: {
            stacked: false,
            title: {
              display: true,
              text: t('js.charts.common.hour'),
              color: theme.mutedColor,
            },
            grid: { color: theme.gridColor, drawBorder: false },
            ticks: {
              color: theme.mutedColor,
              maxRotation: 0,
              autoSkip: false,
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: t('js.charts.common.frequency'),
              color: theme.mutedColor,
            },
            grid: { display: false },
            ticks: { precision: 0, color: theme.mutedColor },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            align: 'center',
            labels: {
              color: theme.legendColor,
              boxWidth: 12,
              boxHeight: 12,
              padding: 16,
              font: { size: 12 },
              generateLabels: (chart) => this.generateKindLegendLabels(chart),
            },
            onClick: (_e, legendItem) => this.toggleKind(legendItem.kind),
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            cornerRadius: 6,
            padding: 10,
            filter: (item) => item.raw !== 0 && item.raw !== null,
            callbacks: { label: (context) => this.tooltipLabel(context) },
          },
          datalabels: {
            display: (context) =>
              context.dataset.type === 'scatter' &&
              context.raw &&
              context.raw.y,
            color: '#fff',
            font: { weight: '700' },
            formatter: (value) => value.y,
          },
        },
      },
    });
  }

  buildDataset(dataset) {
    const base = {
      type: dataset.type,
      label: dataset.label,
      data: dataset.data,
      kind: dataset.kind,
      compareId: dataset.compareId,
      compareLabel: dataset.compareLabel,
      hidden: dataset.hidden,
      order: dataset.order,
    };

    this.assignDatasetColors(base);

    if (dataset.type === 'scatter') {
      return {
        ...base,
        pointRadius: 11,
        pointHoverRadius: 12,
        showLine: false,
      };
    }

    return {
      ...base,
      borderWidth: 0,
      barThickness: dataset.compareId === 'aggregate' ? 28 : 14,
      categoryPercentage: 0.92,
      barPercentage: dataset.compareId === 'aggregate' ? 0.9 : 0.65,
    };
  }

  // Bake colours onto the dataset before Chart() caches element backgroundColor;
  // a later update('none') doesn't refresh that cache reliably.
  assignDatasetColors(dataset) {
    if (dataset.compareId === 'aggregate') {
      this.applyAggregateColors(dataset);
    } else {
      this.applyComparatorColors(dataset);
    }
  }

  aggregateColor(kind) {
    return AGGREGATE_COLORS[kind] || null;
  }

  tooltipLabel(context) {
    const compareLabel = context.dataset.compareLabel;
    const labelPrefix = compareLabel
      ? `${compareLabel} · ${context.dataset.label}`
      : context.dataset.label;

    if (context.dataset.type === 'scatter') {
      return `${labelPrefix}: ${context.raw.y}`;
    }
    return `${labelPrefix}: ${context.raw}`;
  }

  // Overrides the base implementation to also respect per-kind visibility
  // toggled via the Chart.js legend below the chart.
  applyVisibility() {
    if (!this.chart) return;

    const hasSelections = this.selectedItems.size > 0;

    this.chart.data.datasets.forEach((dataset, index) => {
      const kindVisible = this.visibleKinds.has(dataset.kind);
      const compareVisible =
        dataset.compareId === 'aggregate'
          ? !hasSelections
          : this.selectedItems.has(dataset.compareId);
      this.chart.setDatasetVisibility(index, kindVisible && compareVisible);
    });

    this.chart.update();
  }

  toggleKind(kind) {
    if (!kind) return;
    if (this.visibleKinds.has(kind)) {
      if (this.visibleKinds.size === 1) return; // keep at least one visible
      this.visibleKinds.delete(kind);
    } else {
      this.visibleKinds.add(kind);
    }
    this.applyVisibility();
  }

  // Collapses the per-org/floor compare datasets back into the three
  // canonical kinds (bedtimes / waketimes / out_of_bed). Each entry's swatch
  // is taken from the aggregate dataset for that kind, and `hidden` reflects
  // the local `visibleKinds` state so Chart.js renders the strike-through
  // look on toggled-off kinds.
  generateKindLegendLabels(chart) {
    const kinds = ['bedtimes', 'waketimes', 'out_of_bed'];
    return kinds
      .map((kind) => {
        const sample = chart.data.datasets.find(
          (d) => d.kind === kind && d.compareId === 'aggregate'
        );
        if (!sample) return null;
        return {
          text: sample.label,
          fillStyle: sample.backgroundColor,
          strokeStyle: sample.borderColor,
          lineWidth: 0,
          hidden: !this.visibleKinds.has(kind),
          kind,
        };
      })
      .filter(Boolean);
  }

  paintChartColors() {
    this.chart.data.datasets.forEach((dataset) => {
      if (dataset.compareId === 'aggregate') {
        this.applyAggregateColors(dataset);
      } else {
        this.applyComparatorColors(dataset);
      }
    });
  }

  applyAggregateColors(dataset) {
    const color = this.aggregateColor(dataset.kind);
    if (!color) return;
    dataset.backgroundColor = color;
    dataset.borderColor = color;
  }

  applyComparatorColors(dataset) {
    const color = this.colorPool.get(dataset.compareId);
    if (!color) return;

    if (dataset.type === 'scatter') {
      dataset.backgroundColor = color;
      dataset.borderColor = color;
      return;
    }

    dataset.backgroundColor = `${color}${TINT_OPACITY[dataset.kind] || '66'}`;
    dataset.borderColor = color;
  }
}
